/**
 * Dependencies
 */
import http from '../../../services/HttpService'
import { normalizeComponents } from '../../../services/DomService.js'

/**
 * Reset the back to the original state
 *
 * @param  {Function} options.commit
 * @return {Promise}
 */
const resetState = ({ commit }) => commit('RESET_STATE')

/**
 * Reorder a set of pages
 *
 * @param  {Function} options.commit
 * @param  {Objec} payload
 * @return {Void}
 */
const reorderPages = ({ commit, state }, payload) => {
    http.put(`dom/sections/${state.data.sections[payload.sectionIndex].id}/re-order/`, {
        ordering: payload.list.map(i => i.id)
    })
    commit('REORDER_PAGES', payload)
}

/**
 * Reorder top-level sections
 *
 * @param  {Function} options.commit
 * @param  {Object} options.state
 * @param  {Object} payload
 * @return {Void}
 */
const reorderSections = ({ commit, state }, payload) => {
    http.put(`dom/digital-oms/${state.data.id}/re-order/`, {
        ordering: payload.map(i => i.id)
    })
    commit('REORDER_SECTIONS', payload)
}

/**
 * Get the OM
 *
 * @param  {Object} state
 * @return {Promise}
 */
const fetchDOM = ({ commit, rootState, state }) => new Promise((resolve, reject) => {
    commit('SET_LOADING')
    http.get(`projects/${rootState.route.params.projectId}/digital-om/`)
        .then(res => {
            let dom = normalizeComponents(state.layouts, res.data.digital_om)
            commit('SET_DOM', dom)
            commit('RESET_LOADING')
            resolve()
        })
        .catch(err => {
            commit('RESET_LOADING')
            reject(err)
        })
})

/**
 * Toggle the navigation menu
 *
 * @param  {Function} options.commit
 * @return {Promise}
 */
const toggleNav = ({ commit }) => commit('TOGGLE_NAV')

/**
 * Toggle the editing state
 *
 * @param  {Function} options.commit
 * @return {Promise}
 */
const toggleEdit = ({ commit }) => commit('TOGGLE_EDIT')

/**
 * Toggle the page selection dialog
 *
 * @param  {Function} options.commit
 * @return {Promise}
 */
const togglePageSelectionModal = ({ commit }) => commit('TOGGLE_PAGE_SELECTION_MODAL')

/**
 * Open the page selection modal
 *
 * @param  {Object} state
 * @param  {Object} payload
 * @return {Void}
 */
const selectPageLayout = ({ commit, dispatch }, payload) => {
    commit('SET_NEW_PAGE_OPTIONS', payload)
    dispatch('togglePageSelectionModal')
}

const getSection = ({ commit }, id) => new Promise((resolve, reject) => {
    http.get(`dom/sections/${id}`)
        .then(res => resolve(res.data))
        .catch(err => reject(err))
})

/**
 * Create a DOM section
 *
 * @param  {Function} options.commit
 * @param  {Object} payload
 * @return {Promise}
 */
const createSection = ({ commit, state }, payload) => new Promise((resolve, reject) => {
    http.post(`dom/digital-oms/${state.data.id}/sections/`, payload)
        .then(res => {
            commit('APPEND_SECTION', {
                digital_om: state.data.id,
                id: res.data.section_id,
                is_visible: payload.is_visible,
                order: state.data.sections.length,
                pages: [],
                title: payload.title
            })
            resolve(res.data.section_id)
        })
        .catch(err => reject(err))
})

const deleteSection = ({ commit, dispatch }, id) => {
    if (window.confirm('Are you sure you want to remove this section?')) {
        http.del(`dom/sections/${id}/`)
            .then(res => {
                commit('DELETE_SECTION', id)
                dispatch('addNotification', {
                    type: 'success',
                    timeout: true,
                    title: 'Success',
                    text: 'The section was successfully removed'
                }, { root: true })
            })
    }
}

const patchSection = ({ commit, dispatch }, payload) => {
    http.patch(`dom/sections/${payload.sectionId}/`, {
        title: payload.title
    })
    .then(res => {
        commit('PATCH_SECTION', {
            sectionIndex: payload.sectionIndex,
            title: payload.title
        })
        dispatch('addNotification', {
            type: 'success',
            timeout: true,
            title: 'Success',
            text: 'The section title was successfully updated'
        }, { root: true })
    })
}

// Page Actions //

/**
 * Create a new page
 *
 * @param  {Function} options.commit
 * @param  {Object} payload
 * @return {Promise}
 */
const createPage = ({ commit, dispatch }, payload) => new Promise((resolve, reject) => {
    http.post(`dom/sections/${payload.sectionId}/pages/`, {
        layout: payload.layout.name,
        title: payload.title
    })
    .then(res => {
        dispatch('getPage', res.data.page_id)
            .then(page => {
                page.components = new Array(payload.layout.components)
                    .fill({})
                    .map((item, idx) => {
                        return {
                            id: null,
                            meta: {},
                            page: page.id,
                            position: idx,
                            type: null,
                            value: null
                        }
                    })
                commit('ADD_PAGE', { sectionId: payload.sectionId, page: page })
                resolve()
            })
    })
    .catch(err => reject(err))
})

/**
 * Get a DOM page by ID
 *
 * @param  {Number} id)
 * @return {Promise}
 */
const getPage = ({ commit }, id) => new Promise((resolve, reject) => {
    http.get(`dom/pages/${id}/`)
        .then(res => resolve(res.data))
        .catch(err => reject(err))
})

/**
 * Patch a DOM page by ID
 *
 * @param  {Number} id)
 * @return {Promise}
 */
const patchPage = ({ commit, dispatch }, payload) => {
    http.patch(`dom/pages/${payload.pageId}/`, {
        layout: payload.layout,
        title: payload.title
    })
    .then(() => {
        dispatch('getSection', payload.sectionId)
            .then(res => {
                commit('PATCH_PAGE', {
                    sectionIndex: res.order,
                    pageIndex: payload.pageIndex,
                    title: payload.title
                })
                dispatch('addNotification', {
                    type: 'success',
                    timeout: true,
                    title: 'Success',
                    text: 'The page was successfully updated'
                }, { root: true })
            })
    })
}

const deletePage = ({ commit, dispatch }, payload) => {
    let confirm = window.confirm('Are you sure you want to delete this page?')
    if (confirm) {
        http.del(`dom/pages/${payload.pageId}`)
            .then(res => {
                dispatch('getSection', payload.sectionId).then(res => {
                    if (res.title.toLowerCase() === 'sectionless') {
                        dispatch('deleteSection', {
                            sectionId: payload.sectionId,
                            sectionIndex: res.order
                        })
                    }
                    commit('DELETE_PAGE', {
                        sectionIndex: res.order,
                        pageIndex: payload.pageIndex
                    })
                })
            })
    }
}

/**
 * Create a component
 *
 * @param  {Function} options.commit
 * @param  {Object} payload)
 * @return {Promise}
 */
const createComponent = ({ commit, dispatch }, payload) => new Promise((resolve, reject) => {
    let page = payload.component.page
    let type = payload.type.replace(/_/, '-')
    http.post(`dom/pages/${page}/components/${type}/`, payload.model)
        .then(res => {
            http.get(`dom/components/${res.data.component_id}/`)
                .then(comp => {
                    dispatch('addNotification', {
                        type: 'success',
                        timeout: true,
                        title: 'Success',
                        text: 'The component was successfully added to the Digital OM'
                    }, { root: true })
                    commit('SET_COMPONENT', comp.data)
                    resolve()
                })
        })
        .catch(err => reject(err))
})

/**
 * Remove a component
 *
 * @param  {Function} options.commit
 * @param  {Object} payload
 * @return {Promise}
 */
const removeComponent = ({ commit, dispatch }, component) => new Promise((resolve, reject) => {
    http.del(`dom/components/${component.id}/`)
        .then(res => {
            commit('SET_COMPONENT', {
                id: null,
                meta: {},
                page: component.page,
                position: component.position,
                type: null,
                value: null
            })
            dispatch('addNotification', {
                type: 'warning',
                timeout: true,
                title: 'Success',
                text: 'The component was successfully removed from the Digital OM'
            }, { root: true })
            resolve()
        })
        .catch(err => reject(err))
})

/**
 * Update a given component and replace its state
 *
 * @param  {Function} options.commit
 * @param  {Function} options.dispatch
 * @param  {Object} payload
 * @return {Promise}
 */
const updateComponent = ({ commit, dispatch }, payload) => new Promise((resolve, reject) => {
    http.patch(`dom/components/${payload.id}/`, payload)
        .then(res => {
            commit('SET_COMPONENT', res.data.data)
            dispatch('addNotification', {
                type: 'success',
                timeout: true,
                title: 'Component Updated',
                text: 'The component was successfully updated'
            }, { root: true })
            resolve()
        })
        .catch(() => {
            dispatch('addNotification', {
                type: 'error',
                timeout: true,
                title: 'Component Failed to Update',
                text: 'The component was not successfully updated'
            }, { root: true })
        })
})

/**
 * Replace a component in-place
 *
 * @param  {Function} options.commit
 * @param  {Object} data
 * @return {Promise}
 */
const replaceComponent = ({ commit }, data) => commit('SET_COMPONENT', data)

export default {
    getPage,
    patchPage,
    deletePage,
    fetchDOM,
    toggleNav,
    toggleEdit,
    resetState,
    createPage,
    reorderPages,
    getSection,
    createSection,
    updateComponent,
    patchSection,
    deleteSection,
    removeComponent,
    createComponent,
    reorderSections,
    selectPageLayout,
    replaceComponent,
    togglePageSelectionModal
}



// WEBPACK FOOTER //
// ./src/state/modules/dom-alt/actions.js